import { StandardEvents } from '@shopify/events';
import { purchaseState } from '@theme/lazy-oaf-purchase-state';
import '@theme/lazy-oaf-option-focus-guard';

class LazyOafPurchase extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.state = {
      giftCard: this.dataset.giftCard === 'true',
      comingSoon: this.dataset.comingSoon === 'true',
      selected: this.dataset.initialSelected === 'true',
      available: this.dataset.initialAvailable === 'true',
      preorder: this.dataset.initialPreorder === 'true',
      quantityBlocked: this.dataset.quantityBlocked === 'true',
    };
    this.generation = 0;
    this.unselectedOptions = new Set([...this.querySelectorAll('fieldset')]
      .filter((fieldset) => !fieldset.querySelector('input:checked'))
      .map((fieldset) => fieldset.querySelector('input')?.dataset.optionName).filter(Boolean));
    this.state.multipleSizes = new Set([...this.querySelectorAll('variant-picker input[data-option-name]')]
      .map((input) => input.dataset.optionName)
      .filter((name) => /\bsize\b/i.test(name))).size > 1;
    this.addEventListener('change', (event) => {
      if (event.target.matches('variant-picker input[type="radio"]')) {
        this.lastChangedOption = event.target.dataset.optionName;
        this.unselectedOptions.delete(this.lastChangedOption);
      }
    }, { capture: true, signal });
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[ref="addToCartButton"]')) return;
      this.guard(event);
    }, { capture: true, signal });
    this.addEventListener('submit', (event) => this.guard(event), { capture: true, signal });
    this.addEventListener(StandardEvents.productSelect, (event) => this.onVariant(event), { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.onCart(event), { signal });
    this.setAttribute('data-enhanced', '');
    this.resizeObserver = new ResizeObserver(() => {
      this.style.setProperty('--purchase-height', `${this.getBoundingClientRect().height}px`);
      if (!this.hasAttribute('data-embedded')) document.body.style.setProperty('--lazy-oaf-purchase-height', `${this.getBoundingClientRect().height}px`);
    });
    this.resizeObserver.observe(this);
    this.render();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    if (!this.hasAttribute('data-embedded')) document.body.style.removeProperty('--lazy-oaf-purchase-height');
    clearTimeout(this.addedTimer);
  }
  guard(event) {
    const state = purchaseState(this.state);
    if (state.disabled || ['unselected', 'selection-error'].includes(state.name)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!state.disabled) {
        const selectors = [...this.querySelectorAll('volt-option-selector')];
        const selector = selectors.find((item) => !item.querySelector('input:checked')) || selectors[0];
        selector?.open({ keyboard: event.type !== 'click' || event.detail === 0 });
      }
    }
  }
  async onVariant(event) {
    const generation = ++this.generation;
    const changedOption = this.lastChangedOption;
    clearTimeout(this.addedTimer);
    this.state.pending = true;
    this.state.added = false;
    this.state.selectionError = false;
    this.message('');
    this.render();
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.generation) return;
      const resource = detail?.resource;
      if (resource?.id) this.dataset.currentVariantId = String(resource.id);
      this.state.selected = this.unselectedOptions.size === 0;
      this.state.invalid = !resource;
      this.state.available = resource?.available === true;
      const nextHost = detail?.html?.querySelector('lazy-oaf-purchase');
      this.state.preorder = nextHost?.dataset.initialPreorder === 'true';
      this.state.comingSoon = nextHost?.dataset.comingSoon === 'true';
      this.state.quantityBlocked = nextHost?.dataset.quantityBlocked === 'true';
      this.state.pending = false;
      // Horizon's other listeners morph the form from the same response first.
      queueMicrotask(() => {
        // Section rendering may supply default values for an incomplete combination.
        // Keep each untouched option unselected until the customer chooses it.
        for (const input of this.querySelectorAll('variant-picker input[type="radio"]')) {
          if (!this.unselectedOptions.has(input.dataset.optionName)) continue;
          input.checked = false;
          input.removeAttribute('checked');
          input.dataset.currentChecked = 'false';
        }
        for (const selector of this.querySelectorAll('volt-option-selector')) selector.refresh?.();
        this.render();
      });
    } catch (error) {
      if (!this.isConnected || generation !== this.generation) return;
      this.state.pending = false;
      this.state.selectionError = true;
      this.state.selected = false;
      for (const input of this.querySelectorAll('variant-picker input[type="radio"]')) {
        if (changedOption && input.dataset.optionName !== changedOption) continue;
        this.unselectedOptions.add(input.dataset.optionName);
        input.checked = false;
        input.removeAttribute('checked');
        input.dataset.currentChecked = 'false';
      }
      for (const selector of this.querySelectorAll('volt-option-selector')) selector.refresh?.();
      this.message('We could not update this size. Please choose it again.');
      this.render();
    }
  }
  async onCart(event) {
    if (!event.target.closest('product-form-component') || !event.promise) return;
    const generation = this.generation;
    this.message('');
    this.state.adding = true;
    this.state.added = false;
    this.render();
    try {
      const result = await event.promise;
      if (!this.isConnected) return;
      this.state.adding = false;
      if (generation === this.generation && !result.detail?.didError) {
        this.state.added = true;
        this.addedTimer = setTimeout(() => { this.state.added = false; this.render(); }, 3000);
      }
    } catch {
      this.state.adding = false;
      if (generation === this.generation) this.message('We could not add this item. Please try again.');
    }
    this.render();
  }
  message(text) {
    const status = this.querySelector('[data-purchase-status]');
    if (status) status.textContent = text;
  }
  render() {
    if (!this.isConnected) return;
    const state = purchaseState(this.state);
    this.dataset.state = state.name;
    const label = state.name === 'ready' && !this.state.preorder && this.hasAttribute('data-embedded') ? 'ADD TO BASKET' : state.label;
    const button = this.querySelector('[ref="addToCartButton"]');
    if (button) {
      // Horizon delegates this click in document capture, before our size guard.
      // This host owns feedback after the cart promise resolves, including Buy Me.
      button.removeAttribute('on:click');
      button.dataset.purchaseLabel = label;
      button.setAttribute('aria-label', label);
      button.disabled = state.disabled;
      button.setAttribute('aria-busy', String(this.state.pending === true || this.state.adding === true));
    }
  }
}
if (!customElements.get('lazy-oaf-purchase')) customElements.define('lazy-oaf-purchase', LazyOafPurchase);
